import { toRequiredNumber } from './Utilities';

const API_SERVER = process.env.API_SERVER || '';

export interface LicenseCollection {
  collection_uuid: string;
  plan_token?: string;
  [key: string]: unknown;
}

export interface LicensePlan {
  id?: number;
  subscription_id?: number;
  user_id?: number;
  school_id?: number;
  plan_token: string;
  sort_order?: number;
  name?: string;
  description?: string;
  collection_ids?: LicenseCollection[];
  [key: string]: unknown;
}

const cache = {
  userLicenses: new Map<number, LicensePlan[]>(),
  assignableUserLicenses: new Map<number, LicensePlan[]>(),
};

function normalizeUserId(userId: number): number {
  const normalizedUserId = toRequiredNumber(userId, 'userId');
  if (normalizedUserId <= 0) {
    throw new Error('userId must be a positive number');
  }

  return normalizedUserId;
}

async function fetchUserLicenses(
  userId: number,
  pathSuffix: string,
  failureMessage: string
): Promise<LicensePlan[]> {
  const normalizedUserId = normalizeUserId(userId);
  const url = `${API_SERVER}/subscriptions/users/${encodeURIComponent(normalizedUserId)}${pathSuffix}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`${failureMessage}: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export async function getUserLicenses(userId: number): Promise<LicensePlan[]> {
  const normalizedUserId = normalizeUserId(userId);

  if (cache.userLicenses.has(normalizedUserId)) {
    return cache.userLicenses.get(normalizedUserId)!;
  }

  const licenses = await fetchUserLicenses(
    normalizedUserId,
    '',
    'Failed to fetch user licenses'
  );
  cache.userLicenses.set(normalizedUserId, licenses);
  return licenses;
}

export async function getExposedUserLicenses(userId: number): Promise<LicensePlan[]> {
  return fetchUserLicenses(userId, '/exposed', 'Failed to fetch exposed user licenses');
}

export async function getAssignableUserLicenses(userId: number): Promise<LicensePlan[]> {
  const normalizedUserId = normalizeUserId(userId);

  if (cache.assignableUserLicenses.has(normalizedUserId)) {
    return cache.assignableUserLicenses.get(normalizedUserId)!;
  }

  const licenses = await fetchUserLicenses(
    normalizedUserId,
    '/assignable',
    'Failed to fetch assignable user licenses'
  );
  cache.assignableUserLicenses.set(normalizedUserId, licenses);
  return licenses;
}

export function getLicensesForTemplateParents(
  licenses: LicensePlan[],
  templateParentCollectionIds?: readonly string[]
): LicensePlan[] {
  const topParentCollectionId = templateParentCollectionIds?.[0];
  if (!topParentCollectionId) {
    return [];
  }

  return licenses.filter((license) => {
    return license.collection_ids?.some((collection) => {
      return collection.collection_uuid === topParentCollectionId;
    });
  });
}

export function clearLicensingCaches(): void {
  cache.userLicenses.clear();
  cache.assignableUserLicenses.clear();
}

const LicensingService = {
  getUserLicenses,
  getExposedUserLicenses,
  getAssignableUserLicenses,
  getLicensesForTemplateParents,
  clearLicensingCaches,
};

export default LicensingService;
